import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Avatar,
  IconButton,
  Divider,
  Stack,
  Paper,
  Fab,
  Snackbar,
} from "@mui/material";
import {
  LocationOn,
  CalendarToday,
  BookmarkBorder,
  Share,
  ArrowBack,
  Business,
  Schedule,
  WorkOutline,
  Bookmark,
  Send,
  KeyboardArrowLeft,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { COLORS } from "colors/color";
import axios from "axios";
import { PostButton } from "components/Buttons/Buttons";
import { CancelButton } from "components/Buttons/Buttons";
import ButtonAppBar from "components/Navbars/navBar";
import { NextButton } from "components/Buttons/Buttons";
import NewAppBar from "components/Navbars/AppBar";
import Footer from "views/Landing/Footer";
import colors from "colors/color";
import { NewPostButton } from "components/Buttons/Buttons";
import { NewSearchButton } from "components/Buttons/Buttons";
import QRCodeDialog from "views/Posts/QRCodeDialog";

const PRIMARY_BLUE = "#0017E7";
const BORDER_GRAY = "#D9D9D9";

const MainContainer = styled(Box)(({ theme }) => ({
  background: "#fff",
  minHeight: "100vh",
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(4),
}));

const JobCard = styled(Paper)(({ theme }) => ({
  background: "#fff",
  backdropFilter: "blur(20px)",
  borderRadius: 32,
  border: `1px solid rgba(217, 217, 217, 0.3)`,
  // boxShadow: "0 20px 60px rgba(0, 23, 231, 0.12)",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "6px",
    // background: `linear-gradient(90deg, ${PRIMARY_BLUE} 0%, #4285f4 100%)`,
    background: `#fff`,
  },
}));

const HeroSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 4, 4, 4),
  textAlign: "center",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "60px",
    height: "2px",
    background: BORDER_GRAY,
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(4, 3, 3, 3),
  },
}));

const CompanyAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  margin: "0 auto 24px auto",
  background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, #4285f4 100%)`,
  fontSize: "2.5rem",
  boxShadow: `0 12px 40px ${PRIMARY_BLUE}30`,
  border: "4px solid white",
}));

const FloatingChip = styled(Chip)(({ theme }) => ({
  fontFamily: "Poppins, sans-serif",
  fontWeight: 700,
  fontSize: "0.9rem",
  borderRadius: 20,
  background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, #4285f4 100%)`,
  color: "white",
  padding: theme.spacing(0.5, 1),
  boxShadow: `0 8px 25px ${PRIMARY_BLUE}10`,
  border: "none",
  position: "absolute",
  top: 20,
  right: 20,
  [theme.breakpoints.down("sm")]: {
    position: "static",
    marginTop: theme.spacing(2),
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 4, 4, 4),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0, 3, 3, 3),
  },
}));

const InfoBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2, 3),
  borderRadius: 16,
  background: "rgba(0, 23, 231, 0.05)",
  border: `1px solid rgba(0, 23, 231, 0.1)`,
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(0, 23, 231, 0.08)",
    transform: "translateY(-2px)",
  },
}));

const DescriptionBox = styled(Box)(({ theme }) => ({
  background: "rgba(249, 250, 251, 0.8)",
  borderRadius: 20,
  padding: theme.spacing(3),
  border: `1px solid ${BORDER_GRAY}50`,
  position: "relative",
  overflow: "hidden",
}));

const RequirementChip = styled(Chip)(({ theme }) => ({
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  borderRadius: 12,
  background: "white",
  color: PRIMARY_BLUE,
  border: `2px solid ${PRIMARY_BLUE}20`,
  margin: theme.spacing(0.5),
  transition: "all 0.3s ease",
  "&:hover": {
    background: `${PRIMARY_BLUE}10`,
    transform: "scale(1.05)",
    boxShadow: `0 4px 12px ${PRIMARY_BLUE}20`,
  },
  "& .MuiChip-label": {
    fontSize: "1rem", // Override global span size
  },
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  justifyContent: "center",
  marginTop: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  fontFamily: "Poppins, sans-serif",
  borderRadius: 16,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1.5, 4),
  background: "white",
  color: "#666666",
  border: `2px solid ${BORDER_GRAY}`,
  minWidth: 140,
  "&:hover": {
    background: "#f8f9fa",
    borderColor: "#999999",
    transform: "translateY(-1px)",
  },
  transition: "all 0.3s ease",
}));

const PrimaryApplyButton = styled(Button)(({ theme }) => ({
  fontFamily: "Poppins, sans-serif",
  background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, #4285f4 100%)`,
  color: "white",
  borderRadius: 20,
  textTransform: "none",
  fontWeight: 700,
  fontSize: "1.1rem",
  padding: theme.spacing(2.5, 6),
  minWidth: 200,
  boxShadow: `0 12px 40px ${PRIMARY_BLUE}40`,
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    background: `linear-gradient(135deg, ${PRIMARY_BLUE}dd 0%, #4285f4dd 100%)`,
    boxShadow: `0 16px 50px ${PRIMARY_BLUE}50`,
    transform: "translateY(-3px)",
  },
  "&:disabled": {
    background: BORDER_GRAY,
    color: "#999999",
    boxShadow: "none",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
    transition: "left 0.5s",
  },
  "&:hover::before": {
    left: "100%",
  },
  transition: "all 0.4s ease",
}));

const BackFloatingButton = styled(Fab)(({ theme }) => ({
  position: "fixed",
  top: 100,
  left: 24,
  background: "white",
  color: PRIMARY_BLUE,
  boxShadow: `0 8px 25px ${PRIMARY_BLUE}20`,
  zIndex: 1000,
  "&:hover": {
    background: `${PRIMARY_BLUE}10`,
  },
  [theme.breakpoints.down("sm")]: {
    position: "relative",
    top: "auto",
    left: "auto",
    marginBottom: theme.spacing(2),
  },
}));

function ViewJob() {
  const { t } = useTranslation();
  const { postid } = useParams();
  const decodedId = atob(postid);

  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showFull, setShowFull] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [userRole, setUserRole] = useState(getUserRoleFromAccessToken());
  const [job, setJob] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "success",
    action: null,
  });
  const apiUrl = process.env.REACT_APP_API_URL;
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    setIsAuth(!!localStorage.getItem("accessToken"));
  }, []);
  useEffect(() => {
    // Generate QR code URL when post ID changes
    if (postid) {
      const baseUrl = window.location.origin;
      const encodedId = btoa(postid);
      const jobUrl = `${baseUrl}/viewjob/${encodedId}`;
      setQrCodeUrl(jobUrl);
    }
  }, [postid]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openQRCodeDialog, setOpenQRCodeDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const handleMenuClick = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(null);
  };

  const handleShareClick = (event) => {
    event.stopPropagation();
    // Implement share functionality here
    console.log("Share job:", postid);
    setMenuAnchorEl(null);
    setOpenQRCodeDialog(true);
  };

  const getPost = async () => {
    try {
      const response = await axios.get(`${apiUrl}posts/${decodedId}/find`);
      setJob(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching job data:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log("post id is :", decodedId);
    getPost();
  }, [decodedId]);

  useEffect(() => {
    if (job) {
      setIsLoading(false);
    }
  }, [job]);

  if (isLoading || !job) {
    return (
      <MainContainer>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="80vh"
        >
          <CircularProgress 
            sx={{ color: PRIMARY_BLUE, mb: 2 }} 
            size={60} 
            thickness={4}
          />
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              color: "#666666",
              fontWeight: 500,
            }}
          >
            {t("general.loading")}
          </Typography>
        </Box>
      </MainContainer>
    );
  }

  function getUserRoleFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      const userRole = decodedToken.role;
      return userRole;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }

  function parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  }

  const handleApplyClick = () => {
    localStorage.getItem("accessToken")
      ? navigate("/application_page", { state: { job } })
      : (window.location.href = "../login");
  };

  const handleSaveJob = async () => {
    if (isSaved) {
      setSnackbarState({
        open: true,
        message: t("myCard.unsaveConfirmation"),
        severity: "warning",
        action: (
          <>
            <Button
              color="primary"
              size="small"
              onClick={() => {
                confirmUnsave();
                setSnackbarState((prev) => ({ ...prev, open: false }));
              }}
              sx={{ color: "#1976d2", mr: 1 }}
            >
              {t("myCard.yes")}
            </Button>
            <Button
              color="secondary"
              size="small"
              onClick={() =>
                setSnackbarState((prev) => ({ ...prev, open: false }))
              }
              sx={{ color: "#666" }}
            >
              {t("myCard.no")}
            </Button>
          </>
        ),
      });
    } else {
      try {
        const userId = getUserIdFromAccessToken();
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${apiUrl}candidates/save_post/${userId}/${job.id}/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        if (response.ok) {
          setIsSaved(true);
          setSnackbarState({
            open: true,
            message: t("myCard.postSaved"),
            severity: "success",
            action: null,
          });
          setTimeout(() => {
            setSnackbarState((prev) => ({ ...prev, open: false }));
          }, 2000);
        } else {
          throw new Error(data.error);
        }
      } catch (error) {
        console.error("Error saving the post:", error);
      }
    }
  };

  const confirmUnsave = async () => {
    try {
      const userId = getUserIdFromAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}candidates/save_post/${userId}/${job.id}/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to unsave post");
      }

      setIsSaved(false);
      setSnackbarState({
        open: true,
        message: "Post unsaved successfully",
        severity: "success",
        action: null,
      });
    } catch (error) {
      console.error("Error unsaving the post:", error);
    }
  };

  const handleClose = () => {
    setShowForm(false);
  };

  function getUserIdFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      const userId = decodedToken.user_id;
      return userId;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }

  return (
    <>
      <NewAppBar isAuth={isAuth} />
      <MainContainer>
        <Container maxWidth="lg">
          <Box display={{ xs: "block", sm: "none" }}>
            <BackFloatingButton
              size="medium"
              onClick={() => window.history.back()}
            >
              <KeyboardArrowLeft />
            </BackFloatingButton>
          </Box>

          <Box display={{ xs: "none", sm: "block" }}>
            <BackFloatingButton
              onClick={() => window.history.back()}
            >
              <KeyboardArrowLeft />
            </BackFloatingButton>
          </Box>

          <JobCard elevation={0}>
            <FloatingChip label={job.mode} />
            
            <HeroSection>
              <CompanyAvatar
                src={job.companyLogo}
                alt={job.company}
              >
                <Business fontSize="large" />
              </CompanyAvatar>
              
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 800,
                  color: "#1a1a1a",
                  mb: 1,
                  fontSize: { xs: "2rem", md: "3rem" },
                  lineHeight: 1.2,
                }}
              >
                {job.title}
              </Typography>
              
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  color: "#666666",
                  fontWeight: 500,
                  mb: 4,
                }}
              >
                {job.company}
              </Typography>

              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <InfoBadge>
                    <LocationOn sx={{ color: PRIMARY_BLUE, fontSize: 24 }} />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Poppins, sans-serif",
                          color: "#999999",
                          fontWeight: 500,
                        }}
                      >
                        {t("details.location")}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 600,
                          color: "#333333",
                        }}
                      >
                        {job.localisation}
                      </Typography>
                    </Box>
                  </InfoBadge>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoBadge>
                    <WorkOutline sx={{ color: PRIMARY_BLUE, fontSize: 24 }} />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Poppins, sans-serif",
                          color: "#999999",
                          fontWeight: 500,
                        }}
                      >
                        {t("details.jobType")}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 600,
                          color: "#333333",
                        }}
                      >
                        {t("details.job")}
                      </Typography>
                    </Box>
                  </InfoBadge>
                </Grid>
              </Grid>
            </HeroSection>

            <ContentSection>
              <Stack spacing={5}>
                {/* Job Description */}
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 700,
                      color: "#1a1a1a",
                      mb: 3,
                      textAlign: "center",
                    }}
                  >
                    {t("details.description")}
                  </Typography>
                  <DescriptionBox>
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        color: "#444444",
                        lineHeight: 1.8,
                        fontSize: "1.1rem",
                        maxHeight: showFull ? "none" : "150px",
                        overflow: "hidden",
                        position: "relative",
                        "&::after": !showFull
                          ? {
                              content: '""',
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              width: "100%",
                              height: "50px",
                              background:
                                "linear-gradient(to bottom, rgba(249,250,251,0), rgba(249,250,251,1))",
                            }
                          : {},
                      }}
                      dangerouslySetInnerHTML={{ __html: job.description }}
                    >
                       
                      {/* {job.description} */}
                    </Typography>
                    <Box textAlign="center" mt={2}>
                      <Button
                        onClick={() => setShowFull(!showFull)}
                        sx={{
                          textTransform: "none",
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 600,
                          color: PRIMARY_BLUE,
                          "&:hover": {
                            background: `${PRIMARY_BLUE}10`,
                          },
                        }}
                      >
                        {showFull ? t("dataTable.showLess") : t("dataTable.seeMore")}
                      </Button>
                    </Box>
                  </DescriptionBox>
                </Box>

                {/* Requirements */}
                {job.requirements && job.requirements.length > 0 && (
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 700,
                        color: "#1a1a1a",
                        mb: 3,
                        textAlign: "center",
                      }}
                    >
                      {t("details.requiredSkills")}
                    </Typography>
                    <Box 
                      display="flex" 
                      flexWrap="wrap" 
                      justifyContent="center"
                      gap={1}
                    >
                      {job.requirements.map((req, index) => (
                        <RequirementChip key={index} label={req} />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box>
                  <ActionButtonsContainer>
                    <SecondaryButton
                      startIcon={isSaved ? <Bookmark /> : <BookmarkBorder />}
                      onClick={handleSaveJob}
                      sx={{
                        color: isSaved ? PRIMARY_BLUE : "#666666",
                        borderColor: isSaved ? PRIMARY_BLUE : BORDER_GRAY,
                      }}
                    >
                      {isSaved ? t("myCard.postSaved") : t("jobCard.menu.save")}
                    </SecondaryButton>
                    <SecondaryButton onClick={handleShareClick} startIcon={<Share />}>
                      {t("jobCard.menu.share")}
                    </SecondaryButton>
                  </ActionButtonsContainer>
                  
                  <Box textAlign="center" mt={4}>
                    <PrimaryApplyButton
                      onClick={handleApplyClick}
                      disabled={userRole === "r"}
                      size="large"
                      startIcon={<Send />}
                    >
                      {t("jobCard.applyNow")}
                    </PrimaryApplyButton>
                  </Box>
                </Box>
              </Stack>
            </ContentSection>
          </JobCard>
        </Container>
      </MainContainer>
      <Footer />
      <QRCodeDialog
        open={openQRCodeDialog}
        onClose={() => setOpenQRCodeDialog(false)}
        qrCodeUrl={qrCodeUrl}
        formData={job}
        callerComponent="JobCardRec"
      />
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={() => setSnackbarState(prev => ({ ...prev, open: false }))}
        message={snackbarState.message}
        action={snackbarState.action}
      />
    </>
  );
}

export default ViewJob;